import React, { useState, useEffect } from "react";
import { getAuth, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { db, storage } from "../firebase";
import { Form, Button, Alert, Container, Card, Row, Col, Accordion, ProgressBar, Spinner } from "react-bootstrap";
import { collection, addDoc, doc, getDoc, updateDoc, onSnapshot, deleteDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import { useTranslation } from 'react-i18next';
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Component สำหรับจัดการการคลิกบนแผนที่
function LocationMarker({ position, setPosition, setActivity, activity }) {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      setPosition([lat, lng]);
      // อัปเดตค่า latitude และ longitude ในฟอร์ม
      setActivity({
        ...activity,
        latitude: lat.toFixed(6),
        longitude: lng.toFixed(6),
      });
    },
  });

  return position === null ? null : <Marker position={position} />;
}

function AddActivity() {
  const { i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState("");
  const [activity, setActivity] = useState({
    id: "",
    name: "",
    nameEn: "",
    description: "",
    descriptionEn: "",
    date: "",
    location: "",
    locationEn: "",
    latitude: "",
    longitude: "",
    imgURL: "",
    featuredOnHome: false,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [activitiesList, setActivitiesList] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [mapCenter, setMapCenter] = useState([13.736717, 100.523186]); // กรุงเทพฯ
  const [markerPosition, setMarkerPosition] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    // Check Firebase Auth state - ทุกคนที่ login ได้เป็น admin อัตโนมัติ
    const unsubscribe = getAuth().onAuthStateChanged((user) => {
      if (user) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;

    setActivitiesLoading(true);

    // Create Firestore listener with error handling
    const unsubscribe = onSnapshot(
      collection(db, "activities"),
      (snapshot) => {
        try {
          const activitiesData = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              name: data.name || '',
              description: data.description || '',
              date: data.date || '',
              location: data.location || '',
              imgURL: data.imgURL || '',
              category: data.category || '',
              // แปลง latitude/longitude เป็น number เท่านั้น
              latitude: (data.latitude?._lat !== undefined) ? data.latitude._lat : (typeof data.latitude === 'number' ? data.latitude : 0),
              longitude: (data.longitude?._long !== undefined) ? data.longitude._long : (typeof data.longitude === 'number' ? data.longitude : 0)
            };
          });
          setActivitiesList(activitiesData);
          setActivitiesLoading(false);
        } catch (err) {
          console.error("Error processing activities:", err);
          setActivitiesLoading(false);
        }
      },
      (error) => {
        console.error("Firestore listener error:", error);
        setActivitiesLoading(false);
        // Don't show error to user as this is a background operation
      }
    );

    // Cleanup function to unsubscribe when component unmounts
    return () => unsubscribe();
  }, [isAdmin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);

    try {
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists() && userSnap.data().role === "admin") {
        setIsAdmin(true);
        localStorage.setItem("userToken", user.uid);
        // Activities will be fetched automatically by useEffect when isAdmin changes
      } else {
        setError("คุณไม่มีสิทธิ์เข้าถึงหน้านี้");
      }
    } catch (error) {
      setError("เข้าสู่ระบบล้มเหลว: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth).then(() => {
      setIsAdmin(false);
      localStorage.removeItem("userToken");
      setSuccessMessage("");
      setError("");
    }).catch((error) => {
      console.error("Error signing out: ", error);
      setError("เกิดข้อผิดพลาดในการออกจากระบบ");
    });
  };

  const handleChange = (e) => {
    setActivity({ ...activity, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async () => {
    if (!imageFile) {
      return null;
    }

    console.log("🔵 Starting Firebase Storage upload...");
    console.log("📁 File:", imageFile.name, "Size:", imageFile.size, "bytes");

    // Check authentication first
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("❌ User not authenticated");
      setError("⚠️ กรุณาเข้าสู่ระบบก่อนอัปโหลดรูปภาพ");
      return null;
    }
    console.log("✅ User authenticated:", currentUser.email);

    setUploading(true);
    setUploadProgress(0);
    setUploadedBytes(0);
    setTotalBytes(imageFile.size);

    try {
      console.log("📤 Using Cloud Function for upload");

      // ใช้ Cloud Function แทน client-side upload
      const formData = new FormData();
      formData.append('file', imageFile);
      formData.append('folder', 'activities');

      const response = await fetch('https://uploaddonationslip-o3lb6qf5cq-uc.a.run.app', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ Upload successful:", result);

      setUploading(false);
      setUploadProgress(100);

      return result.url;

      /*
      // Old client-side upload code (CORS issues)
      const timestamp = Date.now();
      const fileName = `activities/${timestamp}_${imageFile.name}`;
      console.log("📂 Upload path:", fileName);

      const storageRef = ref(storage, fileName);
      console.log("✅ Storage ref created");

      const uploadTask = uploadBytesResumable(storageRef, imageFile);
      console.log("✅ Upload task created, starting upload...");

      let lastTime = Date.now();
      let lastBytes = 0;
      let progressTimeout = null;

      return new Promise((resolve, reject) => {
        // Set timeout to detect stuck uploads (no progress for 30 seconds)
        const resetProgressTimeout = () => {
          if (progressTimeout) {
            clearTimeout(progressTimeout);
          }
          progressTimeout = setTimeout(() => {
            console.error("⏰ Upload stuck - no progress for 30 seconds");
            setError("⚠️ การอัปโหลดหยุดทำงาน - กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ตและลองใหม่อีกครั้ง");
            setUploading(false);
            setUploadSpeed(0);
            reject(new Error("Upload timeout - no progress"));
          }, 30000); // 30 seconds
        };

        resetProgressTimeout();

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            const state = snapshot.state;
            console.log(`📤 Upload state: ${state}, progress: ${progress.toFixed(1)}% (${snapshot.bytesTransferred}/${snapshot.totalBytes} bytes)`);

            // Reset timeout on each progress update
            resetProgressTimeout();

            setUploadProgress(progress);
            setUploadedBytes(snapshot.bytesTransferred);

            // คำนวณความเร็วในการอัปโหลด
            const currentTime = Date.now();
            const timeDiff = (currentTime - lastTime) / 1000; // วินาที
            const bytesDiff = snapshot.bytesTransferred - lastBytes;

            if (timeDiff > 0.5) {
              const speed = bytesDiff / timeDiff; // bytes per second
              setUploadSpeed(speed);
              console.log(`⚡ Upload speed: ${(speed / 1024).toFixed(2)} KB/s`);
              lastTime = currentTime;
              lastBytes = snapshot.bytesTransferred;
            }
          },
          (error) => {
            if (progressTimeout) {
              clearTimeout(progressTimeout);
            }
            console.error("❌ Upload error:", error);
            console.error("Error code:", error.code);
            console.error("Error message:", error.message);

            // Provide more specific error messages
            let errorMessage = "เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ";
            if (error.code === 'storage/unauthorized') {
              errorMessage = "ไม่มีสิทธิ์ในการอัปโหลด - กรุณาเข้าสู่ระบบใหม่";
            } else if (error.code === 'storage/canceled') {
              errorMessage = "การอัปโหลดถูกยกเลิก";
            } else if (error.code === 'storage/unknown') {
              errorMessage = "เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ - กรุณาลองใหม่อีกครั้ง";
            } else if (error.code === 'storage/retry-limit-exceeded') {
              errorMessage = "เครือข่ายไม่เสถียร - กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต";
            }

            setError(`⚠️ ${errorMessage} (${error.code})`);
            setUploading(false);
            setUploadSpeed(0);
            reject(error);
          },
          async () => {
            if (progressTimeout) {
              clearTimeout(progressTimeout);
            }
            console.log("✅ Upload completed successfully!");
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log("🔗 Download URL:", downloadURL);
              setUploading(false);
              setUploadProgress(0);
              setUploadSpeed(0);
              setUploadedBytes(0);
              setTotalBytes(0);
              resolve(downloadURL);
            } catch (urlError) {
              console.error("❌ Error getting download URL:", urlError);
              setError("⚠️ อัปโหลดสำเร็จแต่ไม่สามารถรับ URL ได้");
              setUploading(false);
              reject(urlError);
            }
          }
        );
      });
      */
    } catch (error) {
      console.error("❌ Upload error:", error);
      setError(`⚠️ เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ: ${error.message || 'Unknown error'}`);
      setUploading(false);
      setUploadSpeed(0);
      setUploadProgress(0);
      return null;
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("⚠️ ขนาดไฟล์ต้องไม่เกิน 5MB");
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError("⚠️ กรุณาเลือกไฟล์รูปภาพเท่านั้น");
        return;
      }
      setImageFile(file);
      setError("");
    }
  };

  const validateForm = () => {
    // ตรวจสอบว่ากรอกข้อมูลครบหรือไม่
    if (!activity.name || !activity.description || !activity.date || !activity.location) {
      setError("⚠️ กรุณากรอกข้อมูลให้ครบทุกช่อง");
      return false;
    }

    // ตรวจสอบ latitude (-90 ถึง 90)
    const lat = parseFloat(activity.latitude);
    if (isNaN(lat) || lat < -90 || lat > 90) {
      setError("⚠️ Latitude ต้องอยู่ระหว่าง -90 ถึง 90");
      return false;
    }

    // ตรวจสอบ longitude (-180 ถึง 180)
    const lng = parseFloat(activity.longitude);
    if (isNaN(lng) || lng < -180 || lng > 180) {
      setError("⚠️ Longitude ต้องอยู่ระหว่าง -180 ถึง 180");
      return false;
    }

    // ตรวจสอบ URL format (ถ้ามี)
    if (activity.imgURL && !activity.imgURL.match(/^https?:\/\/.+/)) {
      setError("⚠️ URL รูปภาพต้องขึ้นต้นด้วย http:// หรือ https://");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setError("");

    console.log("🔵 Form submitted");

    // Validate form
    if (!validateForm()) {
      console.log("❌ Form validation failed");
      return;
    }

    console.log("✅ Form validation passed");
    setLoading(true);

    try {
      let finalImgURL = activity.imgURL;

      // Upload image if a new file is selected
      if (imageFile) {
        console.log("📤 Starting image upload process...");
        const uploadStartTime = Date.now();

        const uploadedURL = await handleImageUpload();

        const uploadEndTime = Date.now();
        const uploadDuration = ((uploadEndTime - uploadStartTime) / 1000).toFixed(2);
        console.log(`⏱️ Image upload took ${uploadDuration} seconds`);

        if (uploadedURL) {
          finalImgURL = uploadedURL;
          console.log("✅ Image uploaded successfully:", uploadedURL);
        } else {
          console.log("⚠️ Image upload returned null");
        }
      } else {
        console.log("ℹ️ No image file to upload");
      }

      console.log("💾 Saving to Firestore...");
      const saveStartTime = Date.now();

      if (isEditing) {
        const activityRef = doc(db, "activities", activity.id);
        await updateDoc(activityRef, {
          ...activity,
          latitude: parseFloat(activity.latitude),
          longitude: parseFloat(activity.longitude),
          imgURL: finalImgURL,
          updatedAt: serverTimestamp(),
        });
        setSuccessMessage("✅ แก้ไขกิจกรรมสำเร็จ!");
        console.log("✅ Activity updated successfully");
      } else {
        await addDoc(collection(db, "activities"), {
          ...activity,
          latitude: parseFloat(activity.latitude),
          longitude: parseFloat(activity.longitude),
          imgURL: finalImgURL,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setSuccessMessage("✅ เพิ่มกิจกรรมสำเร็จ!");
        console.log("✅ Activity added successfully");
      }

      const saveEndTime = Date.now();
      const saveDuration = ((saveEndTime - saveStartTime) / 1000).toFixed(2);
      console.log(`⏱️ Firestore save took ${saveDuration} seconds`);

      setActivity({ id: "", name: "", nameEn: "", description: "", descriptionEn: "", date: "", location: "", locationEn: "", latitude: "", longitude: "", imgURL: "" });
      setImageFile(null);
      setIsEditing(false);
      setMarkerPosition(null);
      setShowMap(false);
    } catch (error) {
      console.error("❌ Error adding/updating document:", error);
      setError("⚠️ เกิดข้อผิดพลาดในการเพิ่ม/แก้ไขข้อมูล");
    } finally {
      setLoading(false);
    }
    setTimeout(() => {
      setSuccessMessage("");
      setError("");
    }, 3000);
  };

  const handleEdit = (activityData) => {
    setActivity(activityData);
    setIsEditing(true);
    setSuccessMessage("");
    setError("");
    // อัปเดตตำแหน่งบนแผนที่
    if (activityData.latitude && activityData.longitude) {
      const lat = parseFloat(activityData.latitude);
      const lng = parseFloat(activityData.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setMarkerPosition([lat, lng]);
        setMapCenter([lat, lng]);
      }
    }
  };

  const handleDelete = async (activityId, activityName) => {
    // ยืนยันก่อนลบ
    const confirmDelete = window.confirm(`คุณแน่ใจหรือไม่ที่จะลบกิจกรรม "${activityName}"?\n\nการกระทำนี้ไม่สามารถย้อนกลับได้!`);

    if (!confirmDelete) {
      return;
    }

    try {
      await deleteDoc(doc(db, "activities", activityId));
      setSuccessMessage(`✅ ลบกิจกรรม "${activityName}" สำเร็จ!`);

      // ถ้ากำลังแก้ไขกิจกรรมที่ลบ ให้ clear form
      if (isEditing && activity.id === activityId) {
        setActivity({ id: "", name: "", nameEn: "", description: "", descriptionEn: "", date: "", location: "", locationEn: "", latitude: "", longitude: "", imgURL: "" });
        setIsEditing(false);
      }

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting document: ", error);
      setError("⚠️ เกิดข้อผิดพลาดในการลบกิจกรรม");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleCancelEdit = () => {
    setActivity({ id: "", name: "", nameEn: "", description: "", descriptionEn: "", date: "", location: "", locationEn: "", latitude: "", longitude: "", imgURL: "" });
    setImageFile(null);
    setIsEditing(false);
    setSuccessMessage("");
    setError("");
    setMarkerPosition(null);
  };

  // ฟังก์ชันค้นหาสถานที่โดยใช้ Nominatim (OpenStreetMap Geocoding)
  const handleSearchLocation = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setError("⚠️ กรุณาใส่ชื่อสถานที่ที่ต้องการค้นหา");
      return;
    }

    setSearching(true);
    setSearchResults([]);
    setError("");

    try {
      // ใช้ Nominatim API (ฟรี, ไม่ต้อง API key)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=5&countrycodes=th&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'DonationCenterApp/1.0'
          }
        }
      );

      if (!response.ok) {
        throw new Error('ไม่สามารถค้นหาสถานที่ได้');
      }

      const data = await response.json();

      if (data.length === 0) {
        setError("⚠️ ไม่พบสถานที่ที่ค้นหา กรุณาลองคำค้นหาอื่น");
      } else {
        setSearchResults(data);
      }
    } catch (error) {
      console.error("Error searching location:", error);
      setError("⚠️ เกิดข้อผิดพลาดในการค้นหาสถานที่");
    } finally {
      setSearching(false);
    }
  };

  // ฟังก์ชันเลือกสถานที่จากผลการค้นหา
  const handleSelectSearchResult = (result) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);

    // อัปเดตตำแหน่งบนแผนที่
    setMarkerPosition([lat, lon]);
    setMapCenter([lat, lon]);

    // อัปเดตฟอร์ม
    setActivity({
      ...activity,
      latitude: lat.toFixed(6),
      longitude: lon.toFixed(6),
      location: result.display_name || activity.location
    });

    // ล้างผลการค้นหา
    setSearchResults([]);
    setSearchQuery("");
    setShowMap(true);
  };



  return (
    <Container fluid className="py-5" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <Row className="justify-content-center">
        <Col md={6} className="mb-4">
          <Card style={{ padding: "30px", boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)", borderRadius: "12px" }}>
            {!isAdmin ? (
              <div>
                <h2 className="text-center mb-4" style={{ color: "#343a40" }}>
                  {i18n.language === 'en' ? 'Login' : 'เข้าสู่ระบบ'}
                </h2>
                {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                <Form onSubmit={handleLogin}>
                  <Form.Group className="mb-3" controlId="formBasicEmail">
                    <Form.Label style={{ fontWeight: "bold" }}>
                      {i18n.language === 'en' ? 'Email' : 'อีเมล'}
                    </Form.Label>
                    <Form.Control
                      type="email"
                      placeholder={i18n.language === 'en' ? 'Enter your email' : 'ใส่อีเมลของคุณ'}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="formBasicPassword">
                    <Form.Label style={{ fontWeight: "bold" }}>
                      {i18n.language === 'en' ? 'Password' : 'รหัสผ่าน'}
                    </Form.Label>
                    <Form.Control
                      type="password"
                      placeholder={i18n.language === 'en' ? 'Enter your password' : 'ใส่รหัสผ่านของคุณ'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                  </Form.Group>

                  <Button variant="primary" type="submit" className="w-100" style={{ backgroundColor: "#007bff", borderColor: "#007bff", fontWeight: "bold" }} disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                        {i18n.language === 'en' ? 'Logging in...' : 'กำลังเข้าสู่ระบบ...'}
                      </>
                    ) : (
                      i18n.language === 'en' ? 'Login' : 'เข้าสู่ระบบ'
                    )}
                  </Button>
                </Form>
              </div>
            ) : (
              <div>
                <h2 className="text-center mb-4" style={{ color: "#28a745" }}>
                  {isEditing
                    ? (i18n.language === 'en' ? 'Edit Activity' : 'แก้ไขกิจกรรม')
                    : (i18n.language === 'en' ? 'Add Activity' : 'เพิ่มกิจกรรม')
                  }
                </h2>
                {successMessage && <Alert variant="success" className="mb-3">{successMessage}</Alert>}
                {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                <Form onSubmit={handleSubmit}>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group controlId="formBasicName">
                        <Form.Label style={{ fontWeight: "bold" }}>
                          {i18n.language === 'en' ? 'Activity Name (Thai)' : 'ชื่อกิจกรรม (ไทย)'} *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          placeholder={i18n.language === 'en' ? 'Activity Name' : 'ชื่อกิจกรรม'}
                          value={activity.name}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formBasicNameEn">
                        <Form.Label style={{ fontWeight: "bold" }}>
                          {i18n.language === 'en' ? 'Activity Name (EN)' : 'Activity Name (EN)'} <small className="text-muted">({i18n.language === 'en' ? 'Optional' : 'ไม่บังคับ'})</small>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="nameEn"
                          placeholder="Activity Name"
                          value={activity.nameEn}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group controlId="formBasicDescription">
                        <Form.Label style={{ fontWeight: "bold" }}>รายละเอียด (ไทย) *</Form.Label>
                        <Form.Control as="textarea" name="description" rows={3} placeholder="รายละเอียดกิจกรรม" value={activity.description} onChange={handleChange} required />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formBasicDescriptionEn">
                        <Form.Label style={{ fontWeight: "bold" }}>Description (EN) <small className="text-muted">(Optional)</small></Form.Label>
                        <Form.Control as="textarea" name="descriptionEn" rows={3} placeholder="Activity Description" value={activity.descriptionEn} onChange={handleChange} />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="formBasicDate">
                    <Form.Label style={{ fontWeight: "bold" }}>
                      {i18n.language === 'en' ? 'Date' : 'วันที่'}
                    </Form.Label>
                    <Form.Control type="date" name="date" value={activity.date} onChange={handleChange} required />
                  </Form.Group>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group controlId="formBasicLocation">
                        <Form.Label style={{ fontWeight: "bold" }}>ที่อยู่ (ไทย) *</Form.Label>
                        <Form.Control type="text" name="location" placeholder="สถานที่จัดกิจกรรม" value={activity.location} onChange={handleChange} required />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formBasicLocationEn">
                        <Form.Label style={{ fontWeight: "bold" }}>Location (EN) <small className="text-muted">(Optional)</small></Form.Label>
                        <Form.Control type="text" name="locationEn" placeholder="Activity Location" value={activity.locationEn} onChange={handleChange} />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group controlId="formBasicLatitude">
                        <Form.Label style={{ fontWeight: "bold" }}>ละติจูด (Latitude)</Form.Label>
                        <Form.Control type="text" name="latitude" placeholder="เช่น 13.7563" value={activity.latitude} onChange={handleChange} required />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formBasicLongitude">
                        <Form.Label style={{ fontWeight: "bold" }}>ลองจิจูด (Longitude)</Form.Label>
                        <Form.Control type="text" name="longitude" placeholder="เช่น 100.5018" value={activity.longitude} onChange={handleChange} required />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* ฟอร์มค้นหาสถานที่ */}
                  <Card className="mb-3" style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6" }}>
                    <Card.Body>
                      <h6 style={{ fontWeight: "bold", color: "#495057", marginBottom: "15px" }}>
                        🔍 {i18n.language === 'en' ? 'Search Location' : 'ค้นหาสถานที่'}
                      </h6>
                      <div>
                        <Row>
                          <Col md={9}>
                            <Form.Control
                              type="text"
                              placeholder="ค้นหาสถานที่ เช่น จุฬาลงกรณ์มหาวิทยาลัย, สยามพารากอน, เซ็นทรัลเวิลด์"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              disabled={searching}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleSearchLocation(e);
                                }
                              }}
                            />
                          </Col>
                          <Col md={3}>
                            <Button
                              variant="success"
                              className="w-100"
                              disabled={searching || !searchQuery.trim()}
                              style={{ fontWeight: "bold" }}
                              onClick={handleSearchLocation}
                            >
                              {searching ? (
                                <>
                                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                                  {i18n.language === 'en' ? 'Searching...' : 'ค้นหา...'}
                                </>
                              ) : (
                                i18n.language === 'en' ? '🔍 Search' : '🔍 ค้นหา'
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </div>

                      {/* ผลการค้นหา */}
                      {searchResults.length > 0 && (
                        <div className="mt-3">
                          <small style={{ fontWeight: "bold", color: "#6c757d" }}>
                            พบ {searchResults.length} สถานที่ (คลิกเพื่อเลือก):
                          </small>
                          <div className="list-group mt-2">
                            {searchResults.map((result, index) => (
                              <button
                                key={index}
                                type="button"
                                className="list-group-item list-group-item-action"
                                onClick={() => handleSelectSearchResult(result)}
                                style={{ cursor: "pointer", textAlign: "left" }}
                              >
                                <div style={{ fontWeight: "bold", color: "#007bff" }}>
                                  📍 {result.display_name.split(',')[0]}
                                </div>
                                <small className="text-muted">
                                  {result.display_name}
                                </small>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>

                  {/* ปุ่มแสดง/ซ่อนแผนที่ */}
                  <div className="mb-3">
                    <Button
                      variant={showMap ? "secondary" : "primary"}
                      onClick={() => setShowMap(!showMap)}
                      size="sm"
                      style={{ fontWeight: "bold" }}
                    >
                      {showMap
                        ? (i18n.language === 'en' ? '🗺️ Hide Map' : '🗺️ ซ่อนแผนที่')
                        : (i18n.language === 'en' ? '🗺️ Select Location from Map' : '🗺️ เลือกตำแหน่งจากแผนที่')
                      }
                    </Button>
                  </div>

                  {/* แผนที่แบบ Interactive */}
                  {showMap && (
                    <div className="mb-3" style={{ border: "2px solid #007bff", borderRadius: "8px", overflow: "hidden" }}>
                      <div style={{ padding: "10px", backgroundColor: "#e7f3ff", borderBottom: "1px solid #007bff" }}>
                        <small style={{ fontWeight: "bold", color: "#0056b3" }}>
                          💡 คลิกบนแผนที่เพื่อเลือกตำแหน่ง (Latitude, Longitude จะถูกกรอกอัตโนมัติ)
                        </small>
                      </div>
                      <MapContainer
                        center={mapCenter}
                        zoom={13}
                        style={{ height: "400px", width: "100%" }}
                        key={`${mapCenter[0]}-${mapCenter[1]}`}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <LocationMarker
                          position={markerPosition}
                          setPosition={setMarkerPosition}
                          setActivity={setActivity}
                          activity={activity}
                        />
                      </MapContainer>
                    </div>
                  )}

                  <Form.Group className="mb-3" controlId="formBasicImgURL">
                    <Form.Label style={{ fontWeight: "bold" }}>ลิงก์รูปภาพ (หรืออัปโหลดรูปด้านล่าง)</Form.Label>
                    <Form.Control type="text" name="imgURL" placeholder="URL ของรูปภาพกิจกรรม" value={activity.imgURL} onChange={handleChange} />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="formBasicImageUpload">
                    <Form.Label style={{ fontWeight: "bold" }}>อัปโหลดรูปภาพ (ไม่เกิน 5MB)</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      disabled={uploading}
                    />
                    {imageFile && !uploading && (
                      <Form.Text className="text-success d-block mt-1">
                        ✓ เลือกไฟล์: {imageFile.name} ({(imageFile.size / 1024).toFixed(2)} KB)
                      </Form.Text>
                    )}
                    {uploading && (
                      <div className="mt-3 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="mb-2 d-flex justify-content-between align-items-center">
                          <span style={{ fontWeight: 'bold', color: '#007bff' }}>
                            📤 กำลังอัปโหลด...
                          </span>
                          <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                            {(uploadedBytes / 1024).toFixed(1)} / {(totalBytes / 1024).toFixed(1)} KB
                          </span>
                        </div>
                        <ProgressBar
                          now={uploadProgress}
                          label={`${Math.round(uploadProgress)}%`}
                          animated
                          variant="success"
                          style={{ height: '25px', fontSize: '14px', fontWeight: 'bold' }}
                        />
                        <div className="mt-2 text-center" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          {uploadSpeed > 0 && (
                            <>
                              ความเร็ว: {(uploadSpeed / 1024).toFixed(1)} KB/s
                              {uploadProgress < 100 && uploadSpeed > 0 && (
                                <> • เวลาที่เหลือโดยประมาณ: {Math.ceil((totalBytes - uploadedBytes) / uploadSpeed)} วินาที</>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </Form.Group>

                  {/* Checkbox สำหรับแสดงบนหน้าแรก */}
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      name="featuredOnHome"
                      checked={activity.featuredOnHome}
                      onChange={(e) => setActivity({ ...activity, featuredOnHome: e.target.checked })}
                      label={
                        <span style={{ fontWeight: "bold", color: "#007bff" }}>
                          ⭐ แสดงกิจกรรมนี้บนหน้าแรก (Show on Homepage)
                        </span>
                      }
                    />
                    <Form.Text className="text-muted">
                      เมื่อเลือกตัวเลือกนี้ กิจกรรมจะปรากฏบนหน้าแรกของเว็บไซต์
                    </Form.Text>
                  </Form.Group>

                  <div className="d-flex justify-content-end">
                    {isEditing && (
                      <Button variant="secondary" onClick={handleCancelEdit} className="me-2" style={{ fontWeight: "bold" }}>
                        {i18n.language === 'en' ? 'Cancel' : 'ยกเลิก'}
                      </Button>
                    )}
                    <Button variant={isEditing ? "warning" : "success"} type="submit" style={{ fontWeight: "bold" }} disabled={loading || uploading}>
                      {loading || uploading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                          {uploading
                            ? (i18n.language === 'en' ? 'Uploading...' : 'กำลังอัปโหลด...')
                            : (i18n.language === 'en' ? 'Saving...' : 'กำลังบันทึก...')
                          }
                        </>
                      ) : (
                        isEditing
                          ? (i18n.language === 'en' ? 'Save Changes' : 'บันทึกการแก้ไข')
                          : (i18n.language === 'en' ? 'Save Activity' : 'บันทึกกิจกรรม')
                      )}
                    </Button>
                  </div>
                </Form>
              </div>
            )}
          </Card>
        </Col>

        {isAdmin && (
          <Col md={6}>
            <Card style={{ padding: "30px", boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)", borderRadius: "12px" }}>
            <h2 className="mb-3" style={{ color: "#007bff" }}>
              {i18n.language === 'en' ? 'Edit Activities' : 'แก้ไขกิจกรรม'}
            </h2>
            {activitiesLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">
                  {i18n.language === 'en' ? 'Loading activities...' : 'กำลังโหลดกิจกรรม...'}
                </p>
              </div>
            ) : activitiesList.length > 0 ? (
              <Accordion defaultActiveKey="0">
                {Object.entries(
                  activitiesList.reduce((groups, act) => {
                    const groupName = act.name;
                    if (!groups[groupName]) {
                      groups[groupName] = [];
                    }
                    groups[groupName].push(act);
                    return groups;
                  }, {})
                ).map(([groupName, groupItems], index) => (
                  <Accordion.Item eventKey={index.toString()} key={groupName}>
                    <Accordion.Header>{groupName}</Accordion.Header>
                    <Accordion.Body>
                      <ul className="list-group">
                        {groupItems.map((act) => (
                          <li key={act.id} className="list-group-item d-flex justify-content-between align-items-start flex-wrap">
                            <div>
                              <strong style={{ color: "#343a40" }}>{String(act.description || '')}</strong>
                              <br />
                              <small className="text-muted">
                                {act.date?.toDate
                                  ? act.date.toDate().toLocaleDateString("th-TH", {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                    })
                                  : String(act.date || '')}
                              </small>
                              <br />
                              <span className="text-muted">{String(act.location || '')}</span>
                            </div>
                            <div className="mt-2 mt-sm-0 d-flex gap-2">
                              <Button
                                variant="info"
                                size="sm"
                                onClick={() => handleEdit(act)}
                                style={{ fontWeight: "bold" }}
                              >
                                {i18n.language === 'en' ? 'Edit' : 'แก้ไข'}
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDelete(act.id, act.name)}
                                style={{ fontWeight: "bold" }}
                              >
                                {i18n.language === 'en' ? 'Delete' : 'ลบ'}
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            ) : (
              <p className="text-muted">
                {i18n.language === 'en' ? 'No activities at the moment' : 'ไม่มีกิจกรรมในขณะนี้'}
              </p>
            )}

            </Card>
          </Col>
        )}
      </Row>

      {isAdmin && (
        <div className="fixed-bottom p-3 d-flex justify-content-end">
          <Button
            variant="danger"
            onClick={handleLogout}
            style={{
              padding: "12px 20px",
              fontSize: "16px",
              borderRadius: "8px",
              fontWeight: "bold",
              boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.15)",
            }}
          >
            {i18n.language === 'en' ? 'Logout' : 'ออกจากระบบ'}
          </Button>
        </div>
      )}
    </Container>
  );
}

export default AddActivity;